import cx from 'clsx';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as SendIcon } from '@assets/svg/ic-send.svg';

import ButtonCopyDetails from '@components/common/ButtonCopyDetails/ButtonCopyDetails';
import FilePreview from '@components/common/FilePreview/FilePreview';
import EditorForm from '@components/form/EditorForm/EditorForm';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem, IComment } from '@model/actionItem';
import type { IFile } from '@model/file';

import { COMMENT_FIELDS, commentDefaultValue, schemaComment } from '../../constants/schemaComment';
import type { IAiControls } from '../../model/IAiControls';
import { useCommentSaveMutation } from '../../services/useCommentSaveMutation';
import AIActionButtons from '../AIActionButtons/AIActionButtons';
import AttachFiles from '../AttachFiles/AttachFiles';
import SystemMessage from '../SystemMessage/SystemMessage';
import UserMessage from '../UserMessage/UserMessage';
import ActivitySkeleton from './ActivitySkeleton';
import style from './style.module.scss';

interface IProps {
  actionItem?: IActionItem;
  commentsData?: {
    comments: Array<IComment>;
    commentsInfo: Array<string>;
  };
  controls: IAiControls;
  isCommentsLoading?: boolean;
}

interface IFormValues {
  comment: string;
  attachments: IFile[];
}

const USER_MESSAGE_TOOLTIP_ID = 'user-message-edit-tooltip';

const Activity: React.FC<IProps> = ({ actionItem, commentsData, controls, isCommentsLoading }) => {
  const { t } = useTranslation();
  const { actionItemId } = useParams();

  const { mutate: commentSaveMutate, isLoading: commentSaveIsLoading } = useCommentSaveMutation({
    actionItemId: actionItemId || '',
  });

  const { setValue, control, watch, handleSubmit, reset } = useForm<IFormValues>({
    mode: 'all',
    resolver: schemaComment,
    defaultValues: commentDefaultValue,
  });

  const isLoading = !actionItem || isCommentsLoading || commentSaveIsLoading;

  if (isLoading) return <ActivitySkeleton />;

  const commentsList = commentsData?.comments || [];
  const hasComments = !!commentsList.length;
  const userTimezone = -new Date().getTimezoneOffset();

  const [attachmentsValue, commentValue] = watch([
    COMMENT_FIELDS.ATTACHMENTS as keyof IFormValues,
    COMMENT_FIELDS.COMMENT as keyof IFormValues,
  ]);

  const submitComment = handleSubmit((data) => {
    commentSaveMutate(
      { ...data, type: actionItem.type, action_item_id: actionItem.id },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Comment was added successfully.') });

          reset(commentDefaultValue);
        },
      },
    );
  });

  const removeFile = (file: { url?: string }) => {
    const filteredFilesList = (attachmentsValue as IFile[]).filter(
      (item: { url?: string }) => item?.url !== file.url,
    );

    setValue(COMMENT_FIELDS.ATTACHMENTS as keyof IFormValues, filteredFilesList);
  };

  return (
    <div className={style.activityBlock}>
      <div className={style.blockHead}>
        <div className={style.blockTitle}>
          {t('Comments')}
          <ButtonCopyDetails
            id="copyComments"
            copyDetailsData={commentsData?.commentsInfo}
            tooltipInfo={t('Copy comments.')}
          />
        </div>
      </div>
      <div className={cx(style.blockContainer, { [style.blockWthtBtns]: controls?.isInstPointTypeBoolean })}>
        {controls?.showCommentForm && (
          <>
            <div className={style.activityForm}>
              <div id="tourStep-15" className={style.activityFormWrapper}>
                <EditorForm
                  name={COMMENT_FIELDS.COMMENT}
                  simple={true}
                  height={37}
                  control={control}
                  dataQa={'comment'}
                  type={'woHelperText'}
                  placeholder={t('Write a comment...')}
                />
                <div className={style.commentSendBlock}>
                  <SendIcon
                    className={cx(style.sendIconBtn, {
                      [style.active]: commentValue,
                      [style.disabled]: commentSaveIsLoading || commentValue.length <= 0,
                    })}
                    onClick={submitComment}
                  />
                </div>
              </div>
              <AttachFiles name={COMMENT_FIELDS.ATTACHMENTS} control={control} />
            </div>
            {!!attachmentsValue.length && (
              <div className={style.activityFormAttaches}>
                {(attachmentsValue as IFile[]).map((file: IFile) => (
                  <FilePreview key={file.name} file={file} removeFile={(file) => removeFile(file)} />
                ))}
              </div>
            )}
          </>
        )}
        <div className={style.commentsWrapper}>
          {hasComments ? (
            commentsList.map((comment) =>
              +comment.type === 2 ? (
                <SystemMessage key={comment.id} commentItem={comment} userTimezone={userTimezone} />
              ) : (
                <UserMessage
                  key={comment.id}
                  commentItem={comment}
                  aiType={actionItem.type}
                  userTimezone={userTimezone}
                  tooltipId={USER_MESSAGE_TOOLTIP_ID}
                />
              ),
            )
          ) : (
            <div className={style.emptyCommentBlock}>
              <span className={style.emptyCommentBlockText}>{t('No comments yet.')}</span>
            </div>
          )}
        </div>
      </div>
      {!controls?.isInstPointTypeBoolean && <AIActionButtons actionItem={actionItem} controls={controls} />}
      <Tooltip id={USER_MESSAGE_TOOLTIP_ID} />
    </div>
  );
};

export default Activity;
